import { useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

type Layout = { tops: number[]; heights: number[]; total: number };
const layouts = new WeakMap<HTMLElement, Layout>();

function indexAt(layout: Layout, y: number) {
  let low = 0,
    high = layout.tops.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (layout.tops[middle] <= y) low = middle;
    else high = middle - 1;
  }
  if (
    low + 1 < layout.tops.length &&
    y - layout.tops[low] - layout.heights[low] > layout.tops[low + 1] - y
  )
    return low + 1;
  return low;
}

/** Includes unmounted pages so dragging the scrollbar cannot select stale DOM. */
export function virtualReaderPageAt(viewer: HTMLElement, y: number) {
  const layout = layouts.get(viewer);
  return layout?.tops.length ? indexAt(layout, y) : null;
}

/** Retain page geometry, not thousands of React page/tooltip/observer owners. */
export function ReaderVirtualFlow({
  paths,
  selected,
  scale,
  renderPage,
  leading,
  trailing,
}: {
  paths: string[];
  selected: number;
  scale: number;
  renderPage: (
    index: number,
    width: string,
    onSize: (width: number, height: number) => void,
    height: number,
  ) => ReactNode;
  /** Drawn above the first page, outside the pages (the document before, at the seam). Its measured
   * height moves every page down, so page geometry stays exact. */
  leading?: (width: string) => ReactNode;
  /** Drawn below the last page (the next document's first page). After every page, so it moves none of them. */
  trailing?: (width: string) => ReactNode;
}) {
  const host = useRef<HTMLDivElement>(null);
  const leadRef = useRef<HTMLDivElement>(null);
  const [leadHeight, setLeadHeight] = useState(0);
  const trailRef = useRef<HTMLDivElement>(null);
  const [trailHeight, setTrailHeight] = useState(0);
  const [viewport, setViewport] = useState({ width: 800, height: 900, top: 0 });
  const [ratios, setRatios] = useState<Record<string, number>>({});
  const previousGeometry = useRef<{ layout: Layout; width: number; lead: number } | null>(null);
  const width = Math.max(220, (viewport.width - 16) * scale);
  const layout = useMemo(() => {
    const tops: number[] = [],
      heights: number[] = [];
    let position = 24 + leadHeight;
    for (const path of paths) {
      if (tops.length) position += 34;
      tops.push(position);
      const height = width * (Object.hasOwn(ratios, path) ? ratios[path] : 11 / 8.5);
      heights.push(height);
      position += height;
    }
    return { tops, heights, total: position + 24 };
  }, [paths, ratios, width, leadHeight]);
  const hasTrail = Boolean(trailing);
  useLayoutEffect(() => {
    const height = trailRef.current?.offsetHeight ?? 0;
    if (height !== trailHeight) setTrailHeight(height);
  });
  useLayoutEffect(() => {
    const trail = trailRef.current;
    if (!trail) return;
    const observer = new ResizeObserver(() => setTrailHeight(trail.offsetHeight));
    observer.observe(trail);
    return () => observer.disconnect();
  }, [hasTrail]);
  const hasLead = Boolean(leading);
  // Measured in the same frame it changes (the seam opening is a page tall), so the pages never
  // draw under it; the observer catches later changes such as its image arriving.
  useLayoutEffect(() => {
    const height = leadRef.current?.offsetHeight ?? 0;
    if (height !== leadHeight) setLeadHeight(height);
  });
  useLayoutEffect(() => {
    const lead = leadRef.current;
    if (!lead) return;
    const observer = new ResizeObserver(() => setLeadHeight(lead.offsetHeight));
    observer.observe(lead);
    return () => observer.disconnect();
  }, [hasLead]);
  useLayoutEffect(() => {
    const viewer = host.current?.parentElement;
    if (!viewer) return;
    const measure = () =>
      setViewport((previous) => {
        const next = {
          width: viewer.clientWidth,
          height: viewer.clientHeight,
          top: viewer.scrollTop,
        };
        // Focus/narrow-pane mode hides this viewer without unmounting it. A
        // hidden box is not a 0px document: retaining its last visible geometry
        // lets source navigation use the real page offsets when it is revealed.
        if (next.width === 0 || next.height === 0) return previous;
        return previous.width === next.width &&
          previous.height === next.height &&
          previous.top === next.top
          ? previous
          : next;
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(viewer);
    viewer.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      viewer.removeEventListener('scroll', measure);
    };
  }, []);
  useLayoutEffect(() => {
    const viewer = host.current?.parentElement;
    if (!viewer) return;
    const previous = previousGeometry.current;
    // Loading an earlier landscape/legal page must not move the paper being read.
    // Zoom has its own pointer anchor; only correct newly learned page geometry here.
    // The seam above the first page moves nothing by opening or closing: whoever opens it scrolls to
    // it, and someone reading it keeps reading it.
    if (
      previous &&
      previous.width === width &&
      previous.layout.tops.length === layout.tops.length &&
      layout.tops.length &&
      viewer.scrollTop >= previous.layout.tops[0]
    ) {
      const anchor = indexAt(previous.layout, viewer.scrollTop);
      const correction =
        layout.tops[anchor] - previous.layout.tops[anchor] - (leadHeight - previous.lead);
      // Even assigning the same scrollTop cancels Chromium's smooth Find move.
      if (Math.abs(correction) > 0.01) viewer.scrollTop += correction;
    }
    previousGeometry.current = { layout, width, lead: leadHeight };
    layouts.set(viewer, layout);
    return () => {
      if (layouts.get(viewer) === layout) layouts.delete(viewer);
    };
  }, [layout, width, leadHeight]);
  const first = Math.max(0, indexAt(layout, viewport.top) - 2);
  const last = Math.min(paths.length - 1, indexAt(layout, viewport.top + viewport.height) + 2);
  const indices = new Set<number>();
  for (let index = first; index <= last; index++) indices.add(index);
  if (selected >= 0 && selected < paths.length) indices.add(selected);
  return (
    <div
      ref={host}
      className="reader-flow reader-flow-with-breaks reader-virtual-flow"
      style={{
        position: 'relative',
        height: layout.total + trailHeight,
        minHeight: '100%',
        padding: 0,
        display: 'block',
        overflowAnchor: 'none',
      }}
    >
      {trailing && (
        <div
          ref={trailRef}
          className="reader-flow-trailing"
          style={{
            position: 'absolute',
            top: layout.total - 24,
            left: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'safe center',
          }}
        >
          {trailing(`${width}px`)}
        </div>
      )}
      {leading && (
        <div
          ref={leadRef}
          className="reader-flow-leading"
          style={{
            position: 'absolute',
            top: 24,
            left: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'safe center',
          }}
        >
          {leading(`${width}px`)}
        </div>
      )}
      {[...indices]
        .sort((a, b) => a - b)
        .map((index) => (
          <div
            key={`${index}:${paths[index]}`}
            style={{
              position: 'absolute',
              top: layout.tops[index] - (index ? 34 : 0),
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'safe center',
            }}
          >
            {renderPage(
              index,
              `${width}px`,
              (naturalWidth, naturalHeight) => {
                if (!(naturalWidth > 0 && naturalHeight > 0)) return;
                const ratio = naturalHeight / naturalWidth;
                setRatios((previous) =>
                  previous[paths[index]] === ratio
                    ? previous
                    : { ...previous, [paths[index]]: ratio },
                );
              },
              layout.heights[index],
            )}
          </div>
        ))}
    </div>
  );
}
